




















































































































































import { Component, Prop, Watch } from "vue-property-decorator";
import _ from "lodash";

import { QuestionnaireService, Utils } from "@/core/services";
import {
  QuestionnaireModel,
  QuestionnaireLookups,
  CriticalSkillModel,
  SkillsStrategyModel,
  MultiInputSumModel,
  FileDownloadModel,
} from "@/core/models";
import { SubmitButtonCpt, FirstOpeningQuestionnaireDialogCpt } from "@/core/components";
import AppVue from "@/AppVue.vue";

import { QuestionnaireStep1Cpt, QuestionnaireStep2Cpt, QuestionnaireStep3Cpt, QuestionnaireStep4Cpt } from ".";
import CheckOutAlertCpt from "./CheckOutAlertCpt.vue";

@Component({
  components: {
    CheckOutAlertCpt,
    QuestionnaireStep1Cpt,
    QuestionnaireStep2Cpt,
    QuestionnaireStep3Cpt,
    QuestionnaireStep4Cpt,
    SubmitButtonCpt,
    FirstOpeningQuestionnaireDialogCpt,
  },
})
export default class QuestionnaireFormCpt extends AppVue {
  @Prop() lookups: QuestionnaireLookups;
  @Prop() model: QuestionnaireModel;
  @Prop({ default: false }) loading: boolean;

  hasUnsavedChanges = false;
  windowSize = 3000;
  internalLoading = false;

  @Watch("model", { deep: true })
  modelChanged(val: any, oldVal: any) {
    this.hasUnsavedChanges = true;
  }

  created() {
    this.$eventHub.$on("QNR_GO_TO_SUBSTEP", async (index: number) => await this.goToSubStep(index));
    this.checkLastStep();
  }

  mounted() {
    window.addEventListener("resize", () => this.calculateWindowWidth());
    this.calculateWindowWidth();
  }

  destroyed() {
    window.removeEventListener("resize", () => this.calculateWindowWidth());
  }

  get isLastStep() {
    return this.model.step >= 3;
  }

  get isFirstStep() {
    return this.model.step <= 0;
  }

  get hasSubSteps() {
    return this.model.step === 2;
  }

  get showCheckOutAlert() {
    return this.model.isOpen && (this.isImi || this.isUser) && !this.loading && !this.isCheckedOutByMe();
  }

  get skillCount() {
    return this.model.criticalSkills.length;
  }

  get nonEmptySkills() {
    return _.filter(this.model.criticalSkills, skill => !!skill.name);
  }

  get nonEmptySkillCount() {
    return this.nonEmptySkills.length;
  }

  get currentSkillPageText() {
    const readableSubStep = this.hasSubSteps ? this.model.subStep + 1 : 0;
    return `${readableSubStep || "#"} / ${this.skillCount || "#"}`;
  }

  get isSubmitButtonDisabled() {
    return this.isLastStep || (this.model.step === 1 && this.nonEmptySkillCount === 0);
  }

  get isBackButtonEnabled() {
    return !this.isFirstStep;
  }

  get mainWidth() {
    return `${this.windowSize}px`;
  }

  checkLastStep() {
    // This is to make the last step be set as complete
    if (this.isLastStep) {
      this.model.step++;
    }
  }

  async downloadPlanOnAPage() {
    try {
      await this.updateModel();
      this.internalLoading = true;
      const response: FileDownloadModel = await QuestionnaireService.downloadCriticalSkillsReport(this.model.id);
      await QuestionnaireService.generateCriticalSkillsFile(response);
    } finally {
      this.internalLoading = false;
    }
  }

  async goToStep(nextStep: number) {
    if (nextStep === this.model.step) {
      return;
    }

    if (nextStep === 2 && this.nonEmptySkillCount === 0) {
      return;
    }

    await this.loadStep(nextStep);
  }

  async goToSubStep(nextSubStep: number) {
    if (this.hasSubSteps && nextSubStep === this.model.subStep) {
      return;
    }

    if (this.nonEmptySkillCount === 0) {
      return;
    }

    await this.loadStep(2, nextSubStep);
  }

  async goNext() {
    if (this.isLastStep) {
      // Finish if goNext() called on the last step
      await this.updateModel();
      return;
    }

    const nextStep = this.model.step + 1;

    if (this.hasSubSteps) {
      await this.goToNextSubStep();
    } else if (nextStep === 2) {
      // We're coming from the previous main step, so to go to the last subStep,
      // we need to set the current subStep to -1 first
      this.model.subStep = -1;
      await this.goToNextSubStep();
    } else {
      await this.loadStep(nextStep);
    }
  }

  async goBack() {
    let prevStep = this.model.step - 1;
    if (this.model.step >= 3) {
      prevStep = 2;
    }

    if (this.hasSubSteps) {
      await this.goToPreviousSubStep();
    } else if (prevStep === 2) {
      // We're coming from the last main step, so to go to the last subStep,
      // we need to set the current subStep to 10 first
      this.model.subStep = 10;
      await this.goToPreviousSubStep();
    } else {
      await this.loadStep(prevStep);
    }
  }

  setHasUnsavedChanges(value: boolean) {
    setTimeout(() => {
      this.hasUnsavedChanges = value;
    });
  }

  private async updateModel() {
    this.internalLoading = true;
    if (this.canEdit()) {
      try {
        const response = await QuestionnaireService.putQuestionnaire(this.model);
        _.extend(this.model, response.data);
        this.$notify.success({
          title: "Details saved",
          message: "Questionnaire details saved",
          duration: 1750,
          showClose: false,
        });
        this.setHasUnsavedChanges(false);
      } catch {
        this.$eventHub.$emit("REFRESH_QNR_MODEL");
      }
    }
    this.internalLoading = false;
  }

  private async goToNextSubStep() {
    if (this.isLastNonEmptyCriticalSkillIndex(this.model.subStep)) {
      await this.goToStep(3);
      return;
    }

    const goTo = this.getNextNonEmptyCriticalSkillIndex(this.model.subStep);
    await this.goToSubStep(goTo);
  }

  private async goToPreviousSubStep() {
    if (this.isFirstNonEmptyCriticalSkillIndex(this.model.subStep)) {
      await this.goToStep(1);
      return;
    }

    const goTo = this.getPreviousNonEmptyCriticalSkillIndex(this.model.subStep);
    await this.goToSubStep(goTo);
  }

  private async loadStep(goToStep: number, goToSubStep: number = 0) {
    if (this.model.showOnlyFirstStep && goToStep > 0) {
      goToStep = 3;
      goToSubStep = 0;
      this.$notify({
        title: "Details saved",
        message: "The next stage of the questionnaire will be available at the workshop",
      });
    }

    this.model.step = goToStep;
    this.model.subStep = goToSubStep;
    await this.updateModel();

    // This is to make the last step be set as complete
    this.checkLastStep();
  }

  private getFirstNonEmptyCriticalSkillIndex() {
    return _.findIndex(this.model.criticalSkills, skill => !!skill.name);
  }

  private isFirstNonEmptyCriticalSkillIndex(index: number) {
    return index === this.getFirstNonEmptyCriticalSkillIndex();
  }

  private getNextNonEmptyCriticalSkillIndex(currentIndex: number) {
    return _.findIndex(this.model.criticalSkills, (skill, index) => {
      return index > currentIndex && !!skill.name;
    });
  }

  private getLastNonEmptyCriticalSkillIndex() {
    return _.findLastIndex(this.model.criticalSkills, skill => !!skill.name);
  }

  private isLastNonEmptyCriticalSkillIndex(index: number) {
    return index === this.getLastNonEmptyCriticalSkillIndex();
  }

  private getPreviousNonEmptyCriticalSkillIndex(currentIndex: number) {
    return _.findLastIndex(this.model.criticalSkills, (skill, index) => {
      return index < currentIndex && !!skill.name;
    });
  }

  private calculateWindowWidth() {
    setInterval(() => {
      const element = document.querySelector("main");
      this.windowSize = !!element ? element.clientWidth : 3000;
    }, 100);
  }

  private isCheckedOutByMe() {
    return this.model.checkedOutById === this.currentUserId;
  }

  private canEdit() {
    return (this.isImi || this.isUser) && this.model.isOpen && this.isCheckedOutByMe();
  }
}
